import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { useState } from 'react';

interface RegistrationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: RegistrationData) => void;
}

export interface RegistrationData {
  team: string;
  fullName: string;
  email: string;
  phone: string;
  programmingLanguage: string;
}

const teams = [
  { id: 'team-alpha', name: 'Team Alpha', leader: 'Khaireddine Dalaa' },
  { id: 'team-beta', name: 'Team Beta', leader: 'AbdelAziz Seqqal' },
  { id: 'team-gamma', name: 'Team Gamma', leader: 'Mohammed Kadi' }
];

const programmingLanguages = [
  { 
    id: 'csharp', 
    name: 'C#', 
    icon: 'https://raw.githubusercontent.com/dotnet/brand/main/logo/dotnet-logo.svg',
    color: '#512BD4',
    description: '.NET Development'
  },
  { 
    id: 'java', 
    name: 'Java', 
    icon: 'https://www.vectorlogo.zone/logos/java/java-icon.svg',
    color: '#E76F00',
    description: 'Java Development'
  },
  { 
    id: 'php', 
    name: 'PHP', 
    icon: 'https://www.php.net/images/logos/new-php-logo.svg',
    color: '#777BB4',
    description: 'PHP Development'
  }
];

export default function GeeksLeagueRegistration({ isOpen, onClose, onSubmit }: RegistrationModalProps) {
  const [formData, setFormData] = useState<RegistrationData>({
    team: '',
    fullName: '',
    email: '',
    phone: '',
    programmingLanguage: ''
  });

  const [errors, setErrors] = useState<Partial<RegistrationData>>({});

  const validateForm = () => {
    const newErrors: Partial<RegistrationData> = {};
    
    if (!formData.team) newErrors.team = 'Please select a team';
    if (!formData.fullName) newErrors.fullName = 'Full name is required';
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    if (!formData.phone) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\+?[\d\s-]{8,}$/.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }
    if (!formData.programmingLanguage) newErrors.programmingLanguage = 'Please select your preferred programming language';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
      onClose();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name as keyof RegistrationData]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="relative w-full max-w-lg bg-white rounded-xl shadow-2xl p-6"
          >
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>

            <h2 className="text-2xl font-bold text-gray-900 mb-6">Join GeeksLeague</h2>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Team Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Your Team
                </label>
                <select
                  name="team"
                  value={formData.team}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 rounded-lg border ${
                    errors.team ? 'border-red-500' : 'border-gray-300'
                  } focus:ring-2 focus:ring-purple-500 focus:border-transparent`}
                >
                  <option value="">Choose a team</option>
                  {teams.map(team => (
                    <option key={team.id} value={team.id}>
                      {team.name} (Led by {team.leader})
                    </option>
                  ))}
                </select>
                {errors.team && (
                  <p className="mt-1 text-sm text-red-500">{errors.team}</p>
                )}
              </div>

              {/* Full Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name
                </label>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 rounded-lg border ${
                    errors.fullName ? 'border-red-500' : 'border-gray-300'
                  } focus:ring-2 focus:ring-purple-500 focus:border-transparent`}
                  placeholder="Enter your full name"
                />
                {errors.fullName && (
                  <p className="mt-1 text-sm text-red-500">{errors.fullName}</p>
                )}
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 rounded-lg border ${
                    errors.email ? 'border-red-500' : 'border-gray-300'
                  } focus:ring-2 focus:ring-purple-500 focus:border-transparent`}
                  placeholder="your@email.com"
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-500">{errors.email}</p>
                )}
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 rounded-lg border ${
                    errors.phone ? 'border-red-500' : 'border-gray-300'
                  } focus:ring-2 focus:ring-purple-500 focus:border-transparent`}
                  placeholder="+1234567890"
                />
                {errors.phone && (
                  <p className="mt-1 text-sm text-red-500">{errors.phone}</p>
                )}
              </div>

              {/* Programming Language Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Preferred Programming Language
                </label>
                <div className="grid grid-cols-3 gap-4">
                  {programmingLanguages.map(lang => (
                    <button
                      key={lang.id}
                      type="button"
                      onClick={() => {
                        setFormData(prev => ({ ...prev, programmingLanguage: lang.id }));
                        setErrors(prev => ({ ...prev, programmingLanguage: undefined }));
                      }}
                      className={`relative flex flex-col items-center justify-center p-4 rounded-lg border ${
                        formData.programmingLanguage === lang.id
                          ? `border-[${lang.color}] bg-${lang.id}-50`
                          : 'border-gray-200 hover:border-purple-200 hover:bg-purple-50/50'
                      } transition-all duration-200 group`}
                    >
                      <div className="w-16 h-16 mb-3 transition-transform group-hover:scale-110">
                        <img
                          src={lang.icon}
                          alt={`${lang.name} logo`}
                          className="w-full h-full object-contain"
                        />
                      </div>
                      <span 
                        className={`text-sm font-medium ${
                          formData.programmingLanguage === lang.id
                            ? `text-[${lang.color}]`
                            : 'text-gray-700'
                        }`}
                      >
                        {lang.name}
                      </span>
                      <span className="text-xs text-gray-500 mt-1">{lang.description}</span>
                    </button>
                  ))}
                </div>
                {errors.programmingLanguage && (
                  <p className="mt-1 text-sm text-red-500">{errors.programmingLanguage}</p>
                )}
              </div>

              <button
                type="submit"
                className="w-full px-6 py-3 bg-gradient-to-r from-[#512BD4] to-[#7B61FF] text-white font-semibold rounded-lg hover:from-[#7B61FF] hover:to-[#512BD4] transition-all duration-300 transform hover:-translate-y-1"
              >
                Submit Application
              </button>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
} 